import { useState, useEffect } from 'react';
import { X, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { api } from '../utils/api';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';

export default function ExportScheduleModal({ isOpen, onClose, hierarchy = [] }) {
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [clientId, setClientId] = useState('');
  const [venueId, setVenueId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [includeBilling, setIncludeBilling] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  // Get available venues and locations based on selection
  const selectedClient = hierarchy.find(c => c.id === clientId);
  const venues = selectedClient?.venues || [];
  const selectedVenue = venues.find(v => v.id === venueId);
  const locations = selectedVenue?.locations || [];

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
      setStartDate(format(weekStart, 'yyyy-MM-dd'));
      setEndDate(format(weekEnd, 'yyyy-MM-dd'));
      setClientId('');
      setVenueId('');
      setLocationId('');
      setStatusFilter('ALL');
      setIncludeBilling(false);
      setExportFormat('csv');
      setError('');
    }
  }, [isOpen]);

  // Reset venue/location when client changes
  useEffect(() => {
    setVenueId('');
    setLocationId('');
  }, [clientId]);

  // Reset location when venue changes
  useEffect(() => {
    setLocationId('');
  }, [venueId]);

  const handleQuickRange = (range) => {
    const today = new Date();
    let start, end;

    switch (range) {
      case 'thisWeek':
        start = startOfWeek(today, { weekStartsOn: 1 });
        end = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case 'lastWeek':
        start = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        end = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        start = startOfMonth(today);
        end = endOfMonth(today);
        break;
      case 'last30':
        start = subDays(today, 30);
        end = today;
        break;
      default:
        return;
    }

    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');

    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        status: statusFilter,
        format: exportFormat,
        includeBilling: includeBilling.toString()
      });

      if (locationId) {
        params.append('locationId', locationId);
      } else if (venueId) {
        params.append('venueId', venueId);
      } else if (clientId) {
        params.append('clientId', clientId);
      }

      // For file download, we need to handle the response differently
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/exports/schedule?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Export failed');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `schedule-export.${exportFormat}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      onClose();
    } catch (err) {
      console.error('Export error:', err);
      setError(err.message || 'Failed to export schedule');
    }

    setExporting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Export Schedule</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-5">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="input"
                />
              </div>
            </div>
            {/* Quick Range Buttons */}
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => handleQuickRange('thisWeek')}
                className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                This Week
              </button>
              <button
                type="button"
                onClick={() => handleQuickRange('lastWeek')}
                className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                Last Week
              </button>
              <button
                type="button"
                onClick={() => handleQuickRange('thisMonth')}
                className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                This Month
              </button>
              <button
                type="button"
                onClick={() => handleQuickRange('last30')}
                className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                Last 30 Days
              </button>
            </div>
          </div>

          {/* Filter by Client / Venue / Location */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Filter by Location (optional)</label>
            
            {/* Client */}
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="input"
            >
              <option value="">All Clients</option>
              {hierarchy.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            {/* Venue - only show if client selected */}
            {clientId && venues.length > 0 && (
              <select
                value={venueId}
                onChange={(e) => setVenueId(e.target.value)}
                className="input"
              >
                <option value="">All Venues</option>
                {venues.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            )}

            {/* Location - only show if venue selected */}
            {venueId && locations.length > 0 && (
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                className="input"
              >
                <option value="">All Locations</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status Filter</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="CONFIRMED">Confirmed Only (Accepted)</option>
              <option value="PENDING_CONFIRMED">Pending & Confirmed</option>
              <option value="ALL">All Gigs (except Cancelled)</option>
              <option value="ALL_INCLUDING_CANCELLED">All Gigs (including Cancelled)</option>
            </select>
          </div>

          {/* Include Billing */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeBilling}
                onChange={(e) => setIncludeBilling(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-hub-600 focus:ring-hub-500"
              />
              <span className="text-sm text-gray-700">Include billing amounts</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Shows the rate billed to client for each gig
            </p>
          </div>

          {/* Export Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Format</label>
            <div className="flex gap-4 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={exportFormat === 'csv'}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="w-4 h-4 text-hub-600 focus:ring-hub-500"
                />
                <FileSpreadsheet className="w-4 h-4 text-blue-500" />
                <span className="text-sm">CSV</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  value="xlsx"
                  checked={exportFormat === 'xlsx'}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="w-4 h-4 text-hub-600 focus:ring-hub-500"
                />
                <FileSpreadsheet className="w-4 h-4 text-green-600" />
                <span className="text-sm">Excel</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={exportFormat === 'pdf'}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="w-4 h-4 text-hub-600 focus:ring-hub-500"
                />
                <FileText className="w-4 h-4 text-red-500" />
                <span className="text-sm">PDF</span>
              </label>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="btn-secondary flex-1"
            disabled={exporting}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
            disabled={exporting}
          >
            {exporting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
